/**
 * Aba Curva A sem Ads: anúncios da curva A (80% do faturamento de 30 dias, uma curva por conta — regra do Pré Acordo)
 * que não estão em campanha ativa, ou estão em hold/pausados. A tela NÃO recomenda inscrever — só mostra.
 */
import { memo } from 'react'
import { Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { brl, inteiro, nomeConta, pct, type CurvaALinha } from './logic'
import { Paginacao, Th, Vazio, useOrdenacao } from './ui'

const MOTIVO: Record<string, { label: string; cor: 'default' | 'warning' | 'info' }> = {
  'fora de campanha': { label: 'fora de campanha', cor: 'info' },
  hold: { label: 'hold (estoque/anúncio)', cor: 'warning' },
  paused: { label: 'pausado no Ads', cor: 'default' },
}

const Linha = memo(function Linha({ l }: { l: CurvaALinha }) {
  const m = MOTIVO[l.motivo] || { label: l.motivo, cor: 'default' as const }
  return (
    <TableRow hover>
      <TableCell sx={{ maxWidth: 300 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} noWrap title={l.titulo}>{l.titulo}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          <a href={`https://produto.mercadolivre.com.br/${l.item_id.replace(/^MLB/, 'MLB-')}`} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{l.item_id}</a>
          {' · '}{nomeConta(l.user_id)}
        </Typography>
      </TableCell>
      <TableCell><Chip size="small" variant="outlined" color={m.cor} label={m.label} sx={{ height: 20, fontSize: 11 }} /></TableCell>
      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{brl(l.faturamento_30d)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 12 }}>{inteiro(l.pedidos_30d)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 12 }}>{l.visitas_30d == null ? <Tooltip title="sem visitas coletadas"><span>—</span></Tooltip> : inteiro(l.visitas_30d)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{pct(l.conversao_anuncio, 2)}</TableCell>
    </TableRow>
  )
})

export function AbaCurvaA({ linhas, janela }: { linhas: CurvaALinha[]; janela: { ini: string; fim: string } | null }) {
  const ord = useOrdenacao<CurvaALinha>(linhas, 'faturamento_30d')
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: 13 } }}>
        Lista de quem vende sem Ads. A tela não recomenda inscrever — só mostra. Curva A = 80% do faturamento dos últimos 30 dias
        {janela ? ` (${janela.ini} a ${janela.fim})` : ''}, uma curva por conta, base <b>margens</b> (type pedido). Pedidos com vários anúncios ficam fora do ranking.
      </Alert>
      {!linhas.length ? <Vazio texto="Toda a curva A está em campanha ativa (ou não há coleta ainda)." /> : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <Th<CurvaALinha> k="titulo" label="Anúncio" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
                  <Th<CurvaALinha> k="motivo" label="Motivo" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
                  <Th<CurvaALinha> k="faturamento_30d" label="Faturamento 30 d" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<CurvaALinha> k="pedidos_30d" label="Pedidos 30 d" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<CurvaALinha> k="visitas_30d" label="Visitas 30 d" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
                  <Th<CurvaALinha> k="conversao_anuncio" label="Conv. anúncio" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Pedidos ÷ visitas" />
                </TableRow>
              </TableHead>
              <TableBody>{ord.visiveis.map((l) => <Linha key={`${l.user_id}-${l.item_id}`} l={l} />)}</TableBody>
            </Table>
          </Box>
          <Paginacao total={ord.ordenadas.length} pagina={ord.pagina} setPagina={ord.setPagina} />
        </>
      )}
    </Box>
  )
}
